import React, {useState, useRef, useEffect, CSSProperties} from 'react';
import styles from './Select.module.css';
import {arrowDownIcon} from "../../../assets/icons/icons.ts";
import {cn} from "../../../utils/cn.ts";
import {Icon} from "../../General/Icon/Icon.tsx";
import {LinearLoader} from "../../LinearLoader/LinearLoader.tsx";
import {SelectOption} from "./types/SelectOption.interface.ts";

interface Props {
    options: SelectOption[],
    value: string,
    onChange: (value: string) => void,
    onSelect: (value: string) => void,
    onSelectOption: (value: any) => void,
    placeholder?: string,
    shownArrow?: boolean,
    isLoading?: boolean,
    shownSearchIcon?: boolean,
    rootClassName?: string,
    selectClassName?: string,
    inputClassName?: string,
    styles?: string|CSSProperties
}

export const Select: React.FC<Props> = ({
        rootClassName,
        selectClassName,
        inputClassName,
        options,
        value,
        onChange,
        onSelect,
        onSelectOption,
        shownArrow = true,
        isLoading = true,
        shownSearchIcon = false,
        placeholder = '---',
    }) => {

    const [isOpen, setIsOpen] = useState(false);
    const [isLoad, setIsLoad] = useState(isLoading);
    const [optionsList, setOptionsList] = useState<SelectOption[]>(options);
    const [searchValue, setSearchValue] = useState(value);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        setOptionsList(options);
    }, [options]);

    useEffect(() => {
        setSearchValue(value);
        options.map(option => {
            if(
                option.value.toLowerCase() === value.toLowerCase()
                || option.value.replace(' ', '').toLowerCase() === value.toLowerCase()
                || option.value.replace('-', '_').toLowerCase() === value.toLowerCase()
            ) {
                handleOptionSelect(option.value);
                onSelectOption(option);
            }
        })
    }, [value]);

    useEffect(() => {
        setIsLoad(isLoading);
    }, [isLoading]);

    function handleToggleDropdown(): void {
        inputRef.current?.focus();

        setIsOpen(!isOpen);
    }

    function handleOptionSelect(selectedValue: string): void {
        onSelect(selectedValue);
        setIsOpen(false);
        setSearchValue(selectedValue);
    }

    function handleClickOutside(event: MouseEvent): void {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
            setIsOpen(false);
            setSearchValue(value || '');
        }
    }

    function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>): void {
        setSearchValue(event.target.value);
        onChange(event.target.value);
    }

    return (
        <div ref={containerRef} className={cn(styles.container, rootClassName)}>
            <div className={cn(styles.select, selectClassName, isOpen && styles.isOpen)} onClick={handleToggleDropdown}>
                {shownSearchIcon && <Icon name='search' width={18} height={18} color='#fff' />}
                <input
                    type='text'
                    placeholder={placeholder}
                    className={cn(styles.searchInput, isOpen && styles.isOpen, inputClassName)}
                    ref={inputRef}
                    value={searchValue}
                    onChange={handleSearchChange}
                />
                {shownArrow && (<div
                    className={cn(
                        styles.arrow,
                        isOpen && styles.arrowOpen
                    )}
                >
                    <img width={14} height={16} src={arrowDownIcon} alt='Стрелка вниз'/>
                </div>)}
            </div>

            {isOpen && optionsList.length ?
                (
                <ul className={styles.dropdown}>
                    {optionsList.map((option) => (
                        <li
                            key={option.id}
                            onClick={() => {
                                handleOptionSelect(option.value);
                                onSelectOption(option);
                            }}
                            className={cn(
                                styles.option,
                                value === option.value && styles.optionSelected,
                            )}
                        >
                            {option.value}
                        </li>
                    ))}
                </ul>
            ) : null
            }
            {
                isLoad
                    ? <div className={styles.dropdown}><LinearLoader/></div>
                    : null
            }
        </div>
    );
};